/**
 * Read the custom claims embedded in the Clerk session JWT (configured in
 * Dashboard → Sessions → Customize session token).
 *
 * The shape is described in `src/types/clerk-session.d.ts` and is shared with
 * the backend at `api/lib/clerkAuth.ts`, which reads the same payload server
 * side to skip the `users.getUser` roundtrip on admin gates.
 *
 * Existing hooks in `src/lib/auth.ts` (useIsAdmin, useIsSuperAdmin,
 * isUserApproved) keep reading from `useUser().publicMetadata` for now. This
 * hook is offered as the lower-latency path: `sessionClaims` is available as
 * soon as Clerk hydrates `useAuth()`, before `useUser()` resolves the full
 * profile.
 */

import { useAuth } from "@clerk/clerk-react";

export interface ResolvedSessionClaims {
  membershipLevel: string | null;
  role: string | null;
  email: string | null;
  banned: boolean;
  approved: boolean;
  isSuperAdmin: boolean;
  devilDarkUi: boolean;
  telegramInviteUrl: string | null;
}

const EMPTY: ResolvedSessionClaims = {
  membershipLevel: null,
  role: null,
  email: null,
  banned: false,
  approved: false,
  isSuperAdmin: false,
  devilDarkUi: false,
  telegramInviteUrl: null,
};

function asBool(v: unknown): boolean {
  return v === true || v === "true";
}

function asString(v: unknown): string | null {
  return typeof v === "string" && v.trim() ? v : null;
}

export function useSessionClaims(): ResolvedSessionClaims {
  const { sessionClaims } = useAuth();
  const c = (sessionClaims ?? {}) as CustomJwtSessionClaims;

  if (!sessionClaims) return EMPTY;

  const role = asString(c.role);
  // Super admin: boolean flags OR role claim. Session templates often only set
  // role="super_admin" without is_super_admin — both must authorize the toolbar.
  const isSuperAdmin =
    role === "super_admin" || asBool(c.is_super_admin) || asBool(c.super_admin);

  return {
    membershipLevel: asString(c.lvl) ?? asString(c.membership_level),
    role,
    email: asString(c.email),
    banned: asBool(c.banned),
    approved: asBool(c.approved),
    isSuperAdmin,
    devilDarkUi: asBool(c.devil_dark_ui),
    telegramInviteUrl: asString(c.telegram_invite_url),
  };
}
